import { action, computed, observable } from 'mobx'
import * as Common from '@tfo/common'
import $http from '../utils/$http'
import { Network } from '@tfo/common/src/services'
import { IGroupSummary } from '../models/IGroupSummary'
import { IIndividual } from '@tfo/common/src/models'

export class ReportsStore {
	@observable companyId: number
	@observable userId: number
	@observable hasSubsidaries: boolean = false
	@observable sorting: Common.enums.ReportSorting
	@observable searchString: string
	@observable activeStateFilter: Common.enums.ReportState
	@observable orders: Common.models.IOrderClient[]
	@observable groupOrders: IGroupSummary
	@observable individuals: Common.models.IIndividual[]
	@observable initialDataLoaded: boolean = false
    @observable numberOfDelivered: number = 0
    @observable numberOfOngoing: number = 0
    @observable numberOfWaitingApproval: number = 0

	private network: Network

	constructor(network: Network, companyId: number, userId: number) {
		this.network = network
		this.companyId = companyId
		this.userId = userId
		this.searchString = ''
		this.sorting = Common.enums.ReportSorting.NewestFirst
		this.activeStateFilter = Common.enums.ReportState.Done
		this.orders = []
		this.individuals = []
	}

	public isUnreadForUser(order: Common.models.IOrderClient): boolean {
		return !order.readReceipt?.some(r => r.userId === this.userId)
	}

	@computed public get amountNotReadReports(): number {
		if (this.initialDataLoaded) {
			return this.orders.filter((o: Common.models.IOrderClient) => this.isUnreadForUser(o)).length
		} else {
			return 0
		}
	}

	@action public async init() {
		const concernStructure = await $http.get<Common.models.Company[]>(`/company/flat/${this.companyId}?subsidiaries=true`)
		this.hasSubsidaries = concernStructure?.length > 1

		await this.ensureCorrectIndividualsForCustomer(this.userId)
		await this.ensureOrdersInActiveState(concernStructure)

		await this.mapObjectsToOrders()
	}

	@action public removeOrder(order: Common.models.IOrderClient) {
		if (order) {
			switch(order.state) {
				case Common.models.State.Delivered:
					this.numberOfDelivered--;
					break;
				case Common.models.State.ResearchCompleted:
				case Common.models.State.ResearchInProgress:
				case Common.models.State.WaitingForResearcher:
				case Common.models.State.ConsentGiven:
					this.numberOfOngoing--;
					break;
				case Common.models.State.Received:
				case Common.models.State.WaitingForConsent:
				case Common.models.State.ConsentDeclined:
				case Common.models.State.ConsentWithdrawn:
					this.numberOfWaitingApproval--;
					break;
			}
			this.orders = this.orders.filter(o => o.id !== order.id);
		}
	}

	private async ensureOrdersInActiveState(concernStructure: Common.models.Company[]) {
		const statesToFetch = this.determineStatesToGetOrdersFor();

		//we don't need to fetch order for any additional states.
		if (statesToFetch.length < 1) {
			return;
		}

		this.numberOfDelivered = 0;
		this.numberOfOngoing = 0;
		this.numberOfWaitingApproval = 0;

		const fetchedOrders: Array<Common.models.IOrderClient> = [];
		for (const index in concernStructure) {
			const subsidiary = concernStructure[index]
            const result = await this.getCompanyOrdersByActiveState(subsidiary, statesToFetch)

			this.numberOfDelivered += result.numberOfDelivered;
			this.numberOfOngoing += result.numberOfOngoing;
			this.numberOfWaitingApproval += result.numberOfWaitingApproval;

			fetchedOrders.push(...result.orders.map(o => {
                return { ...o, companyName: subsidiary.name } as Common.models.IOrderClient
           }))
        }

		this.orders.push(...fetchedOrders)
	}

	private async ensureCorrectIndividualsForCustomer(customerId: number) {
		//No need to fetch the individuals again
		if (this.individuals.length > 0) {
			return;
		}

		var individualIds = await $http.get<number[]>(`/order/individualIdsBy/${customerId}`)
		var fetchedIndividuals = await $http.post<IIndividual[]>(`/User/individual-information/many`, individualIds)

		this.individuals = fetchedIndividuals;
	}

	private async getCompanyOrdersByActiveState(company: Common.models.Company, states: Common.models.State[]): Promise<Common.models.ICustomerOrdersActiveState> {
        return await $http.get<any>(`/order/state/${company.id}?${states.map(n => `orderStates=${n}`).join('&')}`)
    }

	private determineStatesToGetOrdersFor(): Common.models.State[] {
		const statesToFetch = new Set(this.getStates())
		const cachedStates = new Set(this.orders.map(o => o.state))

		cachedStates.forEach(s => statesToFetch.delete(s));

		return [...statesToFetch]
	}

	private getStates(): Common.models.State[] {
		switch (this.activeStateFilter) {
			case Common.enums.ReportState.Archived:
				return [
					Common.models.State.Delivered,
					Common.models.State.Deleted
				]
			case Common.enums.ReportState.Ongoing:
				return [
					Common.models.State.ResearchCompleted,
					Common.models.State.ConsentGiven,
					Common.models.State.ResearchInProgress,
					Common.models.State.WaitingForResearcher
				]
			case Common.enums.ReportState.AwaitingApproval:
				return [
					Common.models.State.WaitingForConsent,
					Common.models.State.ConsentWithdrawn,
					Common.models.State.ConsentDeclined,
					Common.models.State.Received
				]
			case Common.enums.ReportState.Done:
				return [
					Common.models.State.Delivered
				]
		}
	}

	@action async getGroupedOrders(groupId: string) {
		const orders = await $http.get<IGroupSummary>(`/group/${groupId}`)
		if (orders && orders.individualInformation.length > 0) {
			this.groupOrders = orders
			return this.groupOrders
		}
	}

	orderRecipientsFilter = order => {
		return (
			order.creatorId === this.userId ||
			(order.orderRecipientUser && order.orderRecipientUser.some(x => x.userId === this.userId))
		)
	}

	@action async mapObjectsToOrders() {
		if (this.orders && this.individuals) {
			this.orders.forEach((order, index) => {
				const individual = this.individuals.find(c => c.id === order.candidateId)
				this.orders[index].candidate = individual
			})
		}

		this.initialDataLoaded = true
	}

	@computed public get getVisibleOrders() {
		let res = this.orders
		res = this.filterViaSearch(res, this.searchString)
		res = this.filterViaState(res, this.activeStateFilter)
		res = this.sortItems(res, this.sorting)
		return res.filter(o => !o.groupId)
	}

	@computed public get getGroupOrders() {
		let res = this.orders
		res = this.filterViaSearch(res, this.searchString)
		res = this.filterViaState(res, this.activeStateFilter)
		res = this.sortItems(res, this.sorting)
		const ordersWithGroupId = res.filter(o => o.groupId)
		const distinctGroupIds = [...new Set(ordersWithGroupId.map(o => o.groupId))]
		const groupedOrders = this.groupOrdersByGroupId(
			ordersWithGroupId,
			distinctGroupIds
		)
		return groupedOrders
	}

	private groupOrdersByGroupId(orders: Common.models.IOrderClient[], ids: string[]) {
		const result = []
		for (const groupid of ids) {
			const group: Common.models.IGroupOrder = {
				id: groupid,
				orders: orders.filter(o => o.groupId === groupid)
			}
			result.push(group)
		}
		return result
	}

	private sortItems(
		orders: Common.models.IOrderClient[],
		sorting: Common.enums.ReportSorting
	): Common.models.IOrderClient[] {
		switch(sorting)  {
			case Common.enums.ReportSorting.NewestFirst:
			default:
				return orders.sort((a, b) => new Date(b.delivered).getTime() - new Date(a.delivered).getTime());
		}
	}

	private filterViaSearch(
		items: Common.models.IOrderClient[],
		searchString: string
	): Common.models.IOrderClient[] {
		searchString = searchString.toLowerCase()
		const matches = (str: string) =>
			str.toLowerCase().indexOf(searchString) > -1

		return items?.filter(
			({
				candidate: { nationalId = {value: '', country: '' }, lastName = '', firstName = '' } = {},
				companyName = ''
			}) =>
				matches(nationalId.value) ||
				matches(firstName) ||
				matches(lastName) ||
				matches(firstName + ' ' + lastName) ||
				matches(lastName + ' ' + firstName) ||
				matches(companyName)
		)
	}

	private filterViaState(
		items: Common.models.IOrderClient[],
		stateFilter: Common.enums.ReportState
	): Common.models.IOrderClient[] {
		return items?.filter(item => {
			switch (item.state) {
				case Common.models.State.Delivered:
					return stateFilter === Common.enums.ReportState.Done || stateFilter === Common.enums.ReportState.Archived
				case Common.models.State.ResearchCompleted:
				case Common.models.State.ConsentGiven:
				case Common.models.State.ResearchInProgress:
				case Common.models.State.WaitingForResearcher:
					return stateFilter === Common.enums.ReportState.Ongoing
				case Common.models.State.WaitingForConsent:
				case Common.models.State.ConsentWithdrawn:
				case Common.models.State.ConsentDeclined:
				case Common.models.State.Received:
					return stateFilter === Common.enums.ReportState.AwaitingApproval
				case Common.models.State.Deleted:
					return stateFilter === Common.enums.ReportState.Archived
			}
			return false
		})
	}

	private getAmountNewOrdersPerFilter(filter: Common.enums.ReportState): number {
		return this.filterViaState(this.orders, filter)?.filter(
			(item: Common.models.IOrderClient) => !item.isRead
		).length
	}

	@computed public get currentSectionHasOrders(): boolean {
		return this.filterViaState(this.orders, this.activeStateFilter)?.length > 0
	}
}
